import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import {
  saveIreferCredentials,
  getIreferCredentials,
  removeIreferCredentials
} from '../../../services/ireferService';

const IreferSection = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    autoLogin: true
  });
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCredentials();
  }, [user]);

  const loadCredentials = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const data = await getIreferCredentials(user?.id);
      setCredentials({
        email: data?.email || '',
        password: '',
        autoLogin: data?.autoLogin !== false
      });
      setVerified(data?.verified || false);
    } catch (err) {
      console.error('Error loading credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!credentials?.email || !credentials?.password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setSaving(true);
      await saveIreferCredentials(
        user?.id,
        credentials?.email,
        credentials?.password,
        credentials?.autoLogin
      );
      setVerified(true);
      setSuccess('✅ iRefer credentials saved!');
      setCredentials({ ...credentials, password: '' });
    } catch (err) {
      setError('Failed to save credentials: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCredentials = async () => {
    if (!window.confirm('Remove iRefer credentials?')) {
      return;
    }

    try {
      setSaving(true);
      await removeIreferCredentials(user?.id);
      setCredentials({ email: '', password: '', autoLogin: true });
      setVerified(false);
      setSuccess('Credentials removed');
    } catch (err) {
      setError('Failed to remove credentials: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="clinical-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="clinical-card p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Link" size={24} color="#3b82f6" />
          <h2 className="text-2xl font-semibold text-foreground">🔗 iRefer.co.uk Integration</h2>
        </div>
        <p className="text-muted-foreground">
          Connect your iRefer.co.uk account for seamless guideline access
        </p>
      </div>

      {/* Benefits Section */}
      <div className="benefits bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-5 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="CheckCircle" size={20} color="#3b82f6" />
          Benefits:
        </h3>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-foreground">
            <Icon name="Check" size={18} color="#10b981" className="mt-0.5 flex-shrink-0" />
            <span>✅ One-click auto-login</span>
          </li>
          <li className="flex items-start gap-2 text-foreground">
            <Icon name="Check" size={18} color="#10b981" className="mt-0.5 flex-shrink-0" />
            <span>✅ Direct guideline navigation</span>
          </li>
          <li className="flex items-start gap-2 text-foreground">
            <Icon name="Check" size={18} color="#10b981" className="mt-0.5 flex-shrink-0" />
            <span>✅ Fact-check recommendations</span>
          </li>
          <li className="flex items-start gap-2 text-foreground">
            <Icon name="Check" size={18} color="#10b981" className="mt-0.5 flex-shrink-0" />
            <span>✅ Search guidelines database</span>
          </li>
        </ul>
      </div>

      {/* Credentials Form */}
      <form onSubmit={handleSaveCredentials} className="credentials-form space-y-4 mb-6">
        <div className="form-group">
          <label className="block text-sm font-medium text-foreground mb-2">
            iRefer.co.uk Email
          </label>
          <input
            type="email"
            value={credentials?.email}
            onChange={(e) => setCredentials({ ...credentials, email: e?.target?.value })}
            placeholder="email@example.com"
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={verified}
          />
          <small className="text-xs text-muted-foreground mt-1 block">
            Email used for iRefer.co.uk login
          </small>
        </div>

        <div className="form-group">
          <label className="block text-sm font-medium text-foreground mb-2">
            iRefer.co.uk Password
          </label>
          <div className="password-wrapper relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={credentials?.password}
              onChange={(e) => setCredentials({ ...credentials, password: e?.target?.value })}
              placeholder="Password"
              className="w-full px-4 py-2 pr-12 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={verified}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          <small className="text-xs text-muted-foreground mt-1 block">
            Encrypted with AES-256
          </small>
        </div>

        <div className="form-group checkbox">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={credentials?.autoLogin}
              onChange={(e) => setCredentials({ ...credentials, autoLogin: e?.target?.checked })}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            Enable auto-login to iRefer.co.uk
          </label>
        </div>

        {verified && (
          <div className="verified-status bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-4 flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="#10b981" />
            <div>
              <strong className="text-green-700 dark:text-green-300 block mb-1">✅ Account Connected</strong>
              <p className="text-sm text-green-600 dark:text-green-400">
                Credentials verified and auto-login enabled
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <div className="form-actions flex gap-3">
          {!verified ? (
            <button
              type="submit"
              disabled={!credentials?.email || !credentials?.password || saving}
              className="flex-1 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <Icon name="Lock" size={18} />
                  <span>🔒 Save & Verify</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRemoveCredentials}
              disabled={saving}
              className="flex-1 bg-error text-white px-6 py-3 rounded-lg font-medium hover:bg-error/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Icon name="Trash2" size={18} />
              <span>🗑️ Remove Credentials</span>
            </button>
          )}
        </div>
      </form>

      {/* Security Notice */}
      <div className="security-notice bg-muted rounded-lg p-5 mb-6">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Lock" size={18} color="#64748b" />
          🔒 Security & Privacy
        </h4>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <Icon name="Shield" size={16} color="#64748b" className="mt-0.5 flex-shrink-0" />
            <span>AES-256 encryption</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Eye" size={16} color="#64748b" className="mt-0.5 flex-shrink-0" />
            <span>Used only for iRefer.co.uk auto-login</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Globe" size={16} color="#64748b" className="mt-0.5 flex-shrink-0" />
            <span>HTTPS/TLS encryption</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Trash2" size={16} color="#64748b" className="mt-0.5 flex-shrink-0" />
            <span>Remove anytime</span>
          </li>
        </ul>
      </div>

      {/* Help Section */}
      <div className="help bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5">
        <h4 className="text-sm font-semibold text-foreground mb-2">Don't have an account?</h4>
        <p className="text-sm text-muted-foreground">
          Create account at{' '}
          <a
            href="https://www.irefer.org.uk/register"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            iRefer.co.uk/register
          </a>
        </p>
      </div>
    </div>
  );
};

export default IreferSection;